/**
 * What's New — the release notes that ship with the portal.
 *
 * This file IS the feature list. It is bundled with the app, so there is no
 * API call and nothing to set up: the notes a user sees are the ones that were
 * in the build they are running.
 *
 * ## Adding an entry
 *
 * Whenever a feature goes out, add a release to the top of `whatsNewReleases`:
 *
 * ```ts
 * {
 *   version: "1.3.123",              // the package.json version it shipped in
 *   date: "2026-09-17",              // ISO date, and what the ordering uses
 *   items: [
 *     {
 *       title: "Short, plain heading",
 *       description: "One or two sentences saying what a user can now do.",
 *     },
 *   ],
 * },
 * ```
 *
 * Write for the people using the portal, not for the team building it: say what
 * changed for them and where to find it, and leave out anything they cannot
 * see. A release with no user-visible change needs no entry at all.
 *
 * Everyone who has already read up to the previous release gets the dialog
 * again the first time they load the portal after a new entry appears, so keep
 * entries to changes worth interrupting someone for.
 */

export interface WhatsNewItem {
  title: string;
  description: string;
}

export interface WhatsNewRelease {
  /** The package.json version this went out in. Also the identity of the release. */
  version: string;
  /** ISO date (YYYY-MM-DD). Releases are shown newest first by this. */
  date: string;
  items: WhatsNewItem[];
}

export const whatsNewReleases: WhatsNewRelease[] = [
  {
    version: "1.3.140",
    date: "2026-10-29",
    items: [
      {
        title: "Uniform for chosen groups",
        description:
          "Uniform has a new Groups tab. Choose a group and tick the items its members are offered; an item ticked for no group is still offered to everyone. People only see the items for their groups in the app, and someone in several of an item's groups sees it once. The Items tab shows who each item is offered to.",
      },
      {
        title: "Everyone's uniform allowances in one place",
        description:
          "Uniform has a new Allocations tab listing everyone against every item, with what they have left this year and how many have been delivered or are on order. Change anyone's allowance in place and save the changes together: leave a box empty for the item's default, or put 0 to stop them requesting it. Filter it to one group, or search by name.",
      },
      {
        title: "Choose whether a diary booking includes weekends",
        description:
          "When a job added to the diary runs over a Saturday or Sunday, an Includes weekends switch now appears. Leave it off to skip the weekend days, or turn it on to book them too. A single day booked on a weekend is booked as before.",
      },
      {
        title: "Completed Jobs report by completion date",
        description:
          "The Completed Jobs report's dates now choose the jobs completed between them, and are labelled Completed From and Completed To. Every figure is for the whole job, so a job invoiced after it was completed shows in full. The boxes along the top now show the number of jobs, Invoiced, Total Costs and Profit, and the only chart is Profit by Department. The Pipeline report's dates still filter transactions as before.",
      },
      {
        title: "Faster Pipeline and Completed Jobs reports",
        description:
          "The Pipeline and Completed Jobs reports, and their scheduled emails, now load much more quickly, especially over a lot of jobs.",
      },
      {
        title: "Invoiced figures exclude VAT",
        description:
          "On the Pipeline and Completed Jobs reports, Invoiced and Invoiced to Date are now before VAT, to match the costs and the job's own billed to date. As a result, profit is lower than before and Remaining Draw is higher.",
      },
      {
        title: "Workings keep their sections when converted",
        description:
          "Converting a quote to a job, or an enquiry to a quote, now carries the workings sections across, with each working in its own section, instead of putting everything under General. Converting an enquiry to a quote now also brings its workings with it, which it hadn't been doing.",
      },
    ],
  },
  {
    version: "1.3.138",
    date: "2026-10-22",
    items: [
      {
        title: "Piggy bank payments",
        description:
          "Management — Piggy Bank can now record money paid out to someone. Click a person and use Record payment to enter the amount and the date it was paid. Each person shows what they've earned, what they've been paid and what they're still owed, and the list is sorted by who is owed the most. A payment recorded by mistake can be removed. Add payment, which gives someone an item from the catalogue, is now called Give item.",
      },
      {
        title: "Part deliveries on purchase orders",
        description:
          "Each line of a published purchase order now has a truck icon to record its delivery, asking how many have arrived, so an order that comes in parts can be tracked line by line. A line shows how many of it have been delivered, and an order shows as Part Delivered until every line has arrived, when it becomes Delivered by itself. Mark as Delivered still marks the whole order in one go.",
      },
      {
        title: "Committed costs",
        description:
          "A job now shows Committed Costs next to Costs to date: what is still to come on its purchase orders that haven't fully arrived, before VAT. A part delivered line counts only what's outstanding. The Pipeline and Completed Jobs reports have a Committed Costs column too, and it's included in each job's Total Costs - and so in its profit.",
      },
      {
        title: "Deleted people hidden in Piggy Bank",
        description:
          "The Piggy Bank list no longer shows people who have been deleted. Turn on Show deleted to see them again.",
      },
    ],
  },
  {
    version: "1.3.137",
    date: "2026-10-15",
    items: [
      {
        title: "Completed Jobs report",
        description:
          "Reports has a new Completed Jobs report. It works just like the Pipeline report, with the same filters, figures, charts and Excel export, but shows only jobs marked as complete. It has its own permission, Completed Jobs, and can also be added to a report schedule.",
      },
      {
        title: "Quotes converted to a job",
        description:
          "A job's Variations tab now has an Additional Quotes Works table underneath. It lists every quote converted to the job, in quote reference order, with its description, value and the date it was converted, plus the total. The job's original quote is tagged, and View opens a quote in a new tab.",
      },
      {
        title: "Piggy Bank",
        description:
          "Under Management — Piggy Bank, people with the Piggy Bank permission can see everyone's piggy bank and what it adds up to. Click a person to see each entry and where it came from: the job a referral was raised from, its enquiry, and the job it became. A catalogue of items can be set up, and Add payment gives an item to someone, recording who gave it and when. A Trends tab charts the totals month by month, for everyone or one person. Items given show in the person's piggy bank in the app once they update it.",
      },
      {
        title: "The right email on a service reminder",
        description:
          "A service reminder's Client Email could show something that wasn't an email address, such as \"00\". It now shows the reminder's contact's email, or else the client's primary contact's. The reminder also names its contact, and the line that said Client Contact now says Client.",
      },
    ],
  },
  {
    version: "1.3.135",
    date: "2026-10-13",
    items: [
      {
        title: "Send a notification to several groups",
        description:
          "Under Management — Notifications, a notification can now be sent to more than one group at once: choose Group(s), then pick as many groups as you need. Anyone in more than one of the chosen groups gets it only once.",
      },
      {
        title: "Referral emails include the details",
        description:
          "Referrals sent from the app's home screen arrived as an email with only the photos. The email now also includes the work being referred, the contact's name, phone, email and address, and who sent it.",
      },
    ],
  },
  {
    version: "1.3.133",
    date: "2026-10-08",
    items: [
      {
        title: "Several files for one notifiable work document",
        description:
          "On a job's Notifiable Works section, a document can now hold as many files as it needs, such as several photos. Upload picks more than one file at once, and Add more adds to what's already there instead of replacing it. Each file has its own remove button, and the Notifiable Works page lists them all.",
      },
      {
        title: "Distribution groups",
        description:
          "Under Management — Groups, a group can now be a Security group, which gives its members the group's permissions as before, or a Distribution group, which is just a list of people to send notifications to and gives no permissions. Existing groups are all Security groups. Turning a group into a Distribution group removes its permissions from anyone who only had them through that group.",
      },
      {
        title: "View-only access to management screens",
        description:
          "On Accounts, Tools and Users, the View permission now lets someone look without changing anything, and Edit is needed to add, change or delete. The other management screens each have a single Manage permission, which both opens and changes them. Management only appears in the menu for people who can open at least one of its options.",
      },
      {
        title: "Bring back deleted or locked users",
        description:
          "Users now has a Deleted & locked users button that lists everyone who can't sign in, with a Restore for each. A restored user can log in again, and keeps the groups and direct reports they had before.",
      },
      {
        title: "Leaving a group now removes its permissions",
        description:
          "Saving a user used to copy every permission from their groups onto the user, so they kept those permissions after leaving the group. Saving now keeps only the permissions ticked on the user. Permissions from groups come from the group alone and go when the user leaves it.",
      },
      {
        title: "Administrator accounts are protected",
        description:
          "Only a portal administrator can change an administrator's account, make someone an administrator, or change a group that makes its members administrators. Anyone else sees these as read-only.",
      },
    ],
  },
  {
    version: "1.3.132",
    date: "2026-10-02",
    items: [
      {
        title: "Notifiable Works page",
        description:
          "A new Notifiable Works page in the menu shows which jobs still need documents for their notifiable works. View it by job, where each notifiable work shows how many of its required documents are done (for example 5/10) and can be expanded to see which are done and which are outstanding. Or view it by notifiable work: pick one, and every job with it is listed with a tick or cross for each document, and whether each is required. It shows only jobs with required documents still to do; switch on Show complete to see the rest.",
      },
    ],
  },
  {
    version: "1.3.131",
    date: "2026-10-01",
    items: [
      {
        title: "Notifiable works on a job",
        description:
          "A job's notifiable works now each appear in a new Notifiable Works section of the job's menu. Open one to see the documents it needs: upload a file against each, or switch it to Completed where no file is needed. A badge beside each work counts the required documents still to do, and turns into a green tick once they're all done. Uploaded files also appear on the job's Files tab.",
      },
      {
        title: "Required documents, in your order",
        description:
          "Under Management — Notifiable Works, each document can now be marked Required, and set as a file upload or as something ticked off with a switch. Only required documents count towards a work's badge, and they are highlighted on the job. Drag the documents, or use the arrows, to set the order they're listed in on a job.",
      },
      {
        title: "Every split line now reaches job costing",
        description:
          "When a Pieces invoice was split across several lines for the same job, only the first line was added to job costing. Every line is now added. Invoices sent before this change are not corrected automatically.",
      },
      {
        title: "Purchase order count shows straight away",
        description:
          "On a job, the number beside Purchase Orders now shows as soon as the job opens, rather than only after clicking it.",
      },
    ],
  },
  {
    version: "1.3.130",
    date: "2026-09-30",
    items: [
      {
        title: "Manage notifiable works and their documents",
        description:
          "Administrators can now add, rename and remove the notifiable works offered on a job, from Management — Notifiable Works. Renaming one renames it on every job that has it, and removing one stops it being offered while jobs that already have it keep it. Each notifiable work also has its own list of documents, which can be added, renamed and deleted there.",
      },
      {
        title: "Manage service types",
        description:
          "Administrators can now add, rename and remove the service types offered on a service reminder, from Management — Service Types. Removing one stops it being offered; reminders that already have it keep it.",
      },
      {
        title: "Saving a user works again",
        description:
          "Saving a user on the Users screen could fail with “The user couldn't be updated” after their Direct Reports had been changed. It now saves as normal.",
      },
    ],
  },
  {
    version: "1.3.129",
    date: "2026-09-28",
    items: [
      {
        title: "Email an invoice to the customer",
        description:
          "An invoice now has an Email button beside Delete, Cancel and Save. It sends the invoice PDF to the contact on the invoice, showing you who it is going to, the subject and the message before you confirm. Once sent, the invoice shows when it was emailed, and Sales has a new Emailed column. Save any changes first — the email goes out from the saved invoice.",
      },
      {
        title: "Invoice email templates",
        description:
          "Each invoice now has an Email template, chosen beside Type, and is emailed with that template's subject and message. New invoices start on the default. Administrators manage the templates under Management — Invoice Email Templates, where placeholders such as {InvoiceNumber} and {ContactName} are filled in for each invoice.",
      },
      {
        title: "Invoices on one page",
        description:
          "Adding or opening an invoice on Sales now opens a full page, laid out like Xero, where the details and every line are edited together — no more adding lines one at a time. Nothing is saved until you press Save, which then sends the whole invoice to Xero. Invoices also have their own Notes tab.",
      },
      {
        title: "Direct Reports and All users",
        description:
          "On Users, “Can view notes for” is now Direct Reports, because it covers more than notes. A manager can see their direct reports' notes and pick them on the reports. The new All users option lets someone see everybody's notes and figures.",
      },
      {
        title: "Choose whose figures the reports show",
        description:
          "Pipeline, Quote Conversion, Quote Lost, Quote & Job Summary and Employee Open Notes now have a user filter. It starts on all users if you have All users ticked, and on just you if you don't, and you can pick any of your direct reports. On Quote Lost it replaces the Assigned To dropdown and lets you pick more than one person.",
      },
      {
        title: "Uniform",
        description:
          "A new Uniform section in the menu tracks uniform requests from Requested through Ordered to Delivered, and lets you raise a request for someone. Each item has a yearly allowance that resets on 1 October, and can be changed per person on their Uniform tab under Users. Engineers can request uniform from the app, and are notified when it is delivered, declined or cancelled. Anyone with the Uniform permission is emailed each new request.",
      },
      {
        title: "Announcements",
        description:
          "Administrators can post a banner that everyone sees at the top of the portal, from Management — Announcements. Choose a priority and, if you like, when it should start and end. Each person can dismiss a banner, and it won't come back for them.",
      },
      {
        title: "Signature questions on forms",
        description:
          "Forms can now include a Signature question. The person filling in the form signs on their phone, and the signature is saved with the response as an image you can open from the form's responses.",
      },
      {
        title: "Survey notes and photos on an enquiry",
        description:
          "An enquiry has a new Survey Notes tab for notes and photos from the site survey, which can also be added from the app. Survey photos appear in the enquiry's files, tagged Survey, and carry through to its quote and job.",
      },
      {
        title: "Attachments on notifications",
        description:
          "When sending a notification from Management — Notifications, you can now attach images or PDFs. People open them from the notification in the app.",
      },
      {
        title: "Delete the rest of a booking in the diary",
        description:
          "Deleting a job from the diary now asks whether to remove only that entry or the rest of the booking as well. The whole-booking option clears the same job and crew from today onwards, across weekends and bank holidays, and shows what will go before you confirm.",
      },
      {
        title: "First hour invoices are now raised from the app",
        description:
          "The Raise first hour invoice switch has gone from the job in the portal. Office users raise it from the job in the app instead, where it goes straight to Xero and is emailed to the customer. The job in the portal still shows once one has been raised.",
      },
    ],
  },
  {
    version: "1.3.127",
    date: "2026-09-17",
    items: [
      {
        title: "Sub-contract agreement PDFs",
        description:
          "Saving a sub-contract agreement now produces its PDF automatically. The PDF icon on a job's Sub-Contracts tab turns red once the file is ready, and clicking it opens the agreement in a new tab. It takes a moment after saving, so use Refresh if the icon is still grey.",
      },
      {
        title: "More detail on a sub-contract agreement",
        description:
          "An agreement now records the supplier's address, the contract basis (fixed lump sum, remeasurable or day works, or anything you type over them), pre-order site meeting minutes dated, programme, contract, and a document register you can add as many lines to as you need. There are also four contacts \u2014 head of construction, project coordinator, project manager and quantity surveyor. The form is now split into tabs so it stays manageable.",
      },
      {
        title: "Invoice Date now shows the invoice's own date",
        description:
          "On Sales and on the Invoices report, the Invoice Date column was showing the date the invoice was raised rather than the invoice date recorded on it. It now shows the invoice date, falling back to the raised date for older invoices that never had one. Filtering by month works again, and a date range may return a slightly different set of invoices than before.",
      },
      {
        title: "Notifiable works when converting an enquiry",
        description:
          "Converting an enquiry to a job now asks for notifiable works, as converting a quote already did. At least one has to be chosen, so a job carries them however it was created.",
      },
    ],
  },
  {
    version: "1.3.126",
    date: "2026-09-16",
    items: [
      {
        title: "Duplicate a form",
        description:
          "Every form now has a Duplicate button on the Forms list. Give the copy a name and it is created with the same sections, questions and answer options, then opened ready to edit. Responses stay with the original. The short code and \u201cgenerate for job\u201d setting are left clear on the copy so it cannot start acting like the form it came from before you have finished changing it.",
      },
      {
        title: "Administrators can schedule a report for other people",
        description:
          "On Reports \u2014 Report Schedules, an administrator can now name who a scheduled report goes to: any number of people, any number of groups, or both. A group is worked out fresh each time the report is sent, so it follows people joining and leaving. Name nobody and it comes to you, exactly as before.",
      },
      {
        title: "Supplier details on a purchase order",
        description:
          "A purchase order now records the supplier's address, who to ask for there, and the supplier's own quote number and date. The address and contact are typed in, since the supplier list carries only a name. All four appear on the generated PDF \u2014 the contact under the supplier as F.A.O.",
      },
      {
        title: "Purchase order delivery and site contact",
        description:
          "Tick \u201cDeliver to Bamfords head office\u201d beside the site and the order is addressed to head office instead of the site. The site contact is now chosen from a list of people rather than taken from the job, and supplies both the name and the telephone number on the order. The form has been reordered to follow the document: supplier first, then the site.",
      },
    ],
  },
  {
    version: "1.3.125",
    date: "2026-09-14",
    items: [
      {
        title: "Have reports emailed to you on a schedule",
        description:
          "Reports \u2014 Report Schedules lets you set any report you can open to arrive in your inbox as a PDF, daily, weekly or monthly. Pick the period it should cover, such as the previous week or month to date, and it is sent to the address you sign in with. Your schedules are listed so you can edit, pause or delete them, and Send now proves one without waiting for its first run.",
      },
      {
        title: "Quote & Job Summary report",
        description:
          "A new report under Reports showing quotes and jobs raised each month against the same months a year earlier, by count and by value, with a breakdown by work type. Quotes and jobs sit on their own tabs, and headline figures cover the year, month and week around a date you choose.",
      },
      {
        title: "Breadcrumbs in the header",
        description:
          "The header now shows where you are, starting from a home icon \u2014 for example Home \u203a Reports \u203a Quote Lost. Every step is clickable, so you can go back to a section without using the menu on the left.",
      },
    ],
  },
  {
    version: "1.3.124",
    date: "2026-09-11",
    items: [
      {
        title: "Purchase order PDFs",
        description:
          "A published purchase order can now be produced as a PDF, ready to send to the supplier. Open the order \u2014 from Purchase Orders or a job's Purchase Orders tab \u2014 and use Generate PDF, and it opens in a new tab. Drafts have no order number yet, so publish first.",
      },
      {
        title: "Engineers told when this week's diary changes",
        description:
          "Moving or deleting a job in the current week now sends the engineers on it a notification on their phone, saying what changed. Work further ahead is left alone, and you are never notified about your own edits.",
      },
      {
        title: "Filter the Quote Lost report by person",
        description:
          "The Quote Lost report now has an Assigned To filter beside the dates, set to All to begin with. It narrows the whole report, so the totals and charts match the person you pick rather than only the list underneath.",
      },
    ],
  },
  {
    version: "1.3.122",
    date: "2026-09-10",
    items: [
      {
        title: "What's New",
        description:
          "Release notes now appear when you sign in after an update, and are always available from the What's New button beside your name.",
      },
      {
        title: "Remember filters",
        description:
          "List screens now keep the filters, sorting and page size you set as you move between sections. It is on by default \u2014 turn it off in Preferences, under your name in the top right, to start each screen clean.",
      },
      {
        title: "Contact details on quotes and jobs",
        description:
          "The contact's name, address, phone number and email now sit in the header of a quote and a job, as they already did on an enquiry \u2014 so you can reach the customer without going looking for them.",
      },
      {
        title: "Remedial works on a job sheet",
        description:
          "A job sheet now records whether remedial works were asked for, together with the engineer's estimated labour and materials. It all reads back on the job's job sheet, so the work can be quoted without chasing the detail.",
      },
      {
        title: "Default adjustment on enquiries and quotes",
        description:
          "A client's default workings adjustment is now applied to new enquiries and quotes, not just jobs. Converting an enquiry to a quote, or a quote to a job, carries the adjustment across with the workings so the totals stay in step.",
      },
      {
        title: "Reasons a quote was lost",
        description:
          "Marking a quote as lost now asks why, and the Quote Lost report breaks those reasons down by count, value and month.",
      },
      {
        title: "Refer work from the app",
        description:
          "The mobile app can now send a referral from its home screen without needing a job to raise it against, with photos attached.",
      },
    ],
  },
];
